import { Node, mergeAttributes } from '@tiptap/core'

export default Node.create({
  name: 'resizableVideo',

  group: 'block',

  atom: true,

  addAttributes() {
    return {
      src: {
        default: null,
      },
      width: {
        default: 560,
      },
      height: {
        default: 315,
      },
      controls: {
        default: true,
      },
      nocookie: {
        default: false,
      },
      align: {
        default: 'left',
      },
      display: {
        default: 'block', // 'block', 'inline'
      },
      caption: {
        default: '',
      },
      captionPosition: {
        default: 'none', // 'top', 'bottom', 'none'
      },
      spacing: {
        default: 'medium', // 'small', 'medium', 'large'
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'iframe[src*="youtube.com"]',
        getAttrs: (element) => ({
          src: element.getAttribute('src'),
          width: element.getAttribute('width') || 560,
          height: element.getAttribute('height') || 315,
        }),
      },
      {
        tag: 'iframe[src*="youtu.be"]',
        getAttrs: (element) => ({
          src: element.getAttribute('src'),
          width: element.getAttribute('width') || 560,
          height: element.getAttribute('height') || 315,
        }),
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    const { width, height, align, display, caption, captionPosition, spacing, ...attrs } = HTMLAttributes
    
    // Determine spacing values
    const spacingMap = {
      small: '1em',
      medium: '1.5em',
      large: '2em'
    }
    const spacingValue = spacingMap[spacing] || spacingMap.medium
    
    // Build inline styles for iframe
    let iframeStyle = 'display: block; border: none; border-radius: 8px; max-width: 100%;'
    
    if (width) {
      iframeStyle += ` width: ${width}px;`
    }
    if (height) {
      iframeStyle += ` height: ${height}px;`
    }
    
    // If no caption, return simple iframe with alignment
    if (!caption || captionPosition === 'none') {
      if (display === 'inline') {
        iframeStyle = iframeStyle.replace('display: block;', 'display: inline-block; vertical-align: middle;')
        
        if (align === 'center') {
          iframeStyle += ' margin: 0 auto;'
        } else if (align === 'right') {
          iframeStyle += ` float: right; margin-left: ${spacingValue};`
        } else if (align === 'left') {
          iframeStyle += ` float: left; margin-right: ${spacingValue};`
        } else {
          iframeStyle += ` margin: 0 ${spacingValue};`
        }
      } else {
        if (align === 'center') {
          iframeStyle += ' margin-left: auto; margin-right: auto;'
        } else if (align === 'right') {
          iframeStyle += ` float: right; margin-left: ${spacingValue}; margin-bottom: ${spacingValue};`
        } else if (align === 'left') {
          iframeStyle += ` float: left; margin-right: ${spacingValue}; margin-bottom: ${spacingValue};`
        }
      }
      
      return [
        'iframe',
        mergeAttributes(attrs, {
          frameborder: '0',
          allowfullscreen: 'true',
          style: iframeStyle
        }),
      ]
    }
    
    // With caption, wrap in figure
    let figureStyle = 'margin: 1em 0; max-width: 100%;'
    
    if (width) {
      figureStyle += ` width: ${width}px;`
    }
    
    if (display === 'inline') {
      figureStyle += ' display: inline-block; vertical-align: middle;'
      
      if (align === 'center') {
        figureStyle += ' margin: 0 auto;'
      } else if (align === 'right') {
        figureStyle += ` margin-left: auto; margin-right: 0;`
      } else if (align === 'left') {
        figureStyle += ` margin-left: 0; margin-right: auto;`
      } else {
        figureStyle += ` margin: 0 ${spacingValue};`
      }
    } else {
      figureStyle += ' display: block;'
      
      if (align === 'center') {
        figureStyle += ' margin-left: auto; margin-right: auto; text-align: center;'
      } else if (align === 'right') {
        figureStyle += ` float: right; margin-left: ${spacingValue}; margin-bottom: ${spacingValue};`
      } else if (align === 'left') {
        figureStyle += ` float: left; margin-right: ${spacingValue}; margin-bottom: ${spacingValue};`
      }
    }
    
    const captionStyle = 'font-size: 0.875em; color: #6b7280; padding: 0.5em; text-align: center; font-style: italic; margin: 0;'
    
    const iframe = [
      'iframe',
      mergeAttributes(attrs, {
        frameborder: '0',
        allowfullscreen: 'true',
        style: iframeStyle
      }),
    ]
    const captionEl = ['figcaption', { style: captionStyle }, caption]
    
    const children = captionPosition === 'top' ? [captionEl, iframe] : [iframe, captionEl]
    
    return ['figure', { 
      style: figureStyle
    }, ...children]
  },

  addNodeView() {
    return ({ HTMLAttributes, getPos, editor }) => {
      const container = document.createElement('div')
      container.className = 'resizable-video-container'
      container.style.position = 'relative'
      container.style.maxWidth = '100%'
      container.style.margin = '1em 0'
      
      // Create inner wrapper for proper positioning
      const videoWrapper = document.createElement('div')
      videoWrapper.className = 'resizable-video-wrapper'
      videoWrapper.style.position = 'relative'
      videoWrapper.style.display = 'inline-block'
      videoWrapper.style.maxWidth = '100%'
      videoWrapper.style.borderRadius = '8px'
      videoWrapper.style.overflow = 'hidden'
      
      const display = HTMLAttributes.display || 'block'
      const align = HTMLAttributes.align || 'left'
      const spacing = HTMLAttributes.spacing || 'medium'
      
      // Determine spacing values
      const spacingMap = {
        small: '0.5em',
        medium: '1em',
        large: '1.5em'
      }
      const spacingValue = spacingMap[spacing] || spacingMap.medium
      
      if (display === 'inline') {
        container.style.display = 'inline-block'
        container.style.verticalAlign = 'middle'
        container.style.float = 'none'
        
        if (align === 'center') {
          container.style.margin = '0 auto'
        } else if (align === 'right') {
          container.style.marginLeft = 'auto'
          container.style.marginRight = '0'
        } else if (align === 'left') {
          container.style.marginLeft = '0'
          container.style.marginRight = 'auto'
        } else {
          container.style.margin = '0 0.5em'
        }
      } else {
        if (align === 'center') {
          container.style.display = 'block'
          container.style.textAlign = 'center'
          container.style.marginLeft = 'auto'
          container.style.marginRight = 'auto'
          container.style.float = 'none'
        } else {
          container.style.display = 'inline-block'
          
          if (align === 'right') {
            container.style.float = 'right'
            container.style.marginLeft = spacingValue
            container.style.marginBottom = spacingValue
          } else if (align === 'left') {
            container.style.float = 'left'
            container.style.marginRight = spacingValue
            container.style.marginBottom = spacingValue
          }
        }
      }

      // Caption elements
      const caption = HTMLAttributes.caption || ''
      const captionPosition = HTMLAttributes.captionPosition || 'none'
      
      let captionEl = null
      if (captionPosition !== 'none') {
        captionEl = document.createElement('div')
        captionEl.className = 'video-caption'
        captionEl.contentEditable = 'true'
        captionEl.textContent = caption || 'Enter caption...'
        captionEl.style.fontSize = '0.875em'
        captionEl.style.color = caption ? '#6b7280' : '#9ca3af'
        captionEl.style.padding = '0.5em'
        captionEl.style.textAlign = 'center'
        captionEl.style.fontStyle = 'italic'
        captionEl.style.outline = 'none'
        captionEl.style.cursor = 'text'
        captionEl.style.minHeight = '2em'
        captionEl.style.userSelect = 'text'
        captionEl.style.background = 'rgba(0, 0, 0, 0.05)'
        captionEl.setAttribute('data-placeholder', 'Enter caption...')
        
        captionEl.addEventListener('focus', (e) => {
          if (e.target.textContent === 'Enter caption...') {
            e.target.textContent = ''
            e.target.style.color = '#6b7280'
          }
        })
        
        captionEl.addEventListener('blur', (e) => {
          const text = e.target.textContent.trim()
          if (text === '' || text === 'Enter caption...') {
            e.target.textContent = 'Enter caption...'
            e.target.style.color = '#9ca3af'
            if (typeof getPos === 'function') {
              const pos = getPos()
              if (pos !== undefined) {
                editor.commands.updateAttributes('resizableVideo', { 
                  caption: '' 
                })
              }
            }
          } else {
            if (typeof getPos === 'function') {
              const pos = getPos()
              if (pos !== undefined) {
                editor.commands.updateAttributes('resizableVideo', { 
                  caption: text 
                })
              }
            }
          }
        })
        
        captionEl.addEventListener('input', (e) => {
          const text = e.target.textContent
          if (text !== 'Enter caption...') {
            e.target.style.color = '#6b7280'
          }
        })
        
        captionEl.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            captionEl.blur()
          }
          e.stopPropagation()
        })
        
        captionEl.addEventListener('mousedown', (e) => {
          e.stopPropagation()
        })
        
        captionEl.addEventListener('click', (e) => {
          e.stopPropagation()
          captionEl.focus()
        })
        
        captionEl.addEventListener('dblclick', (e) => {
          e.stopPropagation()
        })
      }

      const iframe = document.createElement('iframe')
      Object.entries(HTMLAttributes).forEach(([key, value]) => {
        if (value !== null && value !== undefined && !['caption', 'captionPosition', 'display'].includes(key)) {
          iframe.setAttribute(key, value)
        }
      })
      
      iframe.setAttribute('frameborder', '0')
      iframe.setAttribute('allowfullscreen', 'true')
      iframe.style.display = 'block'
      iframe.style.borderRadius = '8px'
      iframe.style.margin = align === 'center' ? '0 auto' : '0'

      // Add resize handle
      const resizeHandle = document.createElement('div')
      resizeHandle.className = 'video-resize-handle'
      resizeHandle.style.position = 'absolute'
      resizeHandle.style.bottom = captionEl && captionPosition === 'bottom' ? '30px' : '0'
      resizeHandle.style.right = '0'
      resizeHandle.style.width = '12px'
      resizeHandle.style.height = '12px'
      resizeHandle.style.background = '#3b82f6'
      resizeHandle.style.cursor = 'se-resize'
      resizeHandle.style.opacity = '0'
      resizeHandle.style.transition = 'opacity 0.2s'
      resizeHandle.style.borderRadius = '3px'
      resizeHandle.style.border = '2px solid white'
      resizeHandle.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.2)'
      resizeHandle.style.zIndex = '10'

      // Assemble container
      if (captionEl && captionPosition === 'top') {
        videoWrapper.appendChild(captionEl)
      }
      videoWrapper.appendChild(iframe)
      if (captionEl && captionPosition === 'bottom') {
        videoWrapper.appendChild(captionEl)
      }
      videoWrapper.appendChild(resizeHandle)
      
      container.appendChild(videoWrapper)

      // Show/hide controls on hover
      videoWrapper.addEventListener('mouseenter', () => {
        resizeHandle.style.opacity = '1'
      })

      videoWrapper.addEventListener('mouseleave', () => {
        resizeHandle.style.opacity = '0'
      })

      // Resize functionality
      let isResizing = false
      let startX, startWidth, startHeight, aspectRatio

      resizeHandle.addEventListener('mousedown', (e) => {
        isResizing = true
        startX = e.clientX
        startWidth = parseInt(iframe.getAttribute('width') || iframe.offsetWidth, 10)
        startHeight = parseInt(iframe.getAttribute('height') || iframe.offsetHeight, 10)
        aspectRatio = startHeight / startWidth
        
        videoWrapper.style.outline = '2px solid #3b82f6'
        videoWrapper.style.outlineOffset = '2px'
        
        document.body.style.userSelect = 'none'
        document.body.style.cursor = 'se-resize'
        
        e.preventDefault()
        e.stopPropagation()
      })

      document.addEventListener('mousemove', (e) => {
        if (!isResizing) return

        e.preventDefault()
        e.stopPropagation()

        const deltaX = e.clientX - startX
        const newWidth = Math.max(200, Math.min(800, startWidth + deltaX))
        const newHeight = Math.round(newWidth * aspectRatio)

        iframe.setAttribute('width', newWidth)
        iframe.setAttribute('height', newHeight)
        iframe.style.width = newWidth + 'px'
        iframe.style.height = newHeight + 'px'
        
        videoWrapper.style.width = newWidth + 'px'
      })

      document.addEventListener('mouseup', () => {
        if (isResizing) {
          isResizing = false
          
          videoWrapper.style.outline = ''
          videoWrapper.style.outlineOffset = ''
          
          document.body.style.userSelect = ''
          document.body.style.cursor = ''
          
          const newWidth = parseInt(iframe.getAttribute('width'), 10)
          const newHeight = parseInt(iframe.getAttribute('height'), 10)
          
          if (typeof getPos === 'function') {
            editor.commands.updateAttributes('resizableVideo', {
              width: newWidth,
              height: newHeight,
            })
          }
        }
      })

      // Make video selectable by clicking
      container.addEventListener('click', (e) => {
        // Don't select if clicking on caption
        if (captionEl && (e.target === captionEl || captionEl.contains(e.target))) {
          return
        }
        
        if (e.target === iframe || e.target === videoWrapper || e.target === container) {
          if (typeof getPos === 'function') {
            const pos = getPos()
            editor.commands.setNodeSelection(pos)
          }
        }
      })

      return {
        dom: container,
        contentDOM: captionEl,
        update: (updatedNode) => {
          if (updatedNode.type.name !== 'resizableVideo') {
            return false
          }

          const newAttrs = updatedNode.attrs
          const oldDisplay = display
          const oldAlign = align
          const oldCaptionPosition = captionPosition
          const oldSpacing = spacing

          // Only force re-render if structural attributes changed
          if (newAttrs.display !== oldDisplay || 
              newAttrs.align !== oldAlign || 
              newAttrs.captionPosition !== oldCaptionPosition ||
              newAttrs.spacing !== oldSpacing) {
            return false
          }

          // Update iframe attributes without re-rendering
          Object.entries(newAttrs).forEach(([key, value]) => {
            if (value !== null && value !== undefined && !['caption', 'captionPosition', 'display'].includes(key)) {
              iframe.setAttribute(key, value)
              if (key === 'width' || key === 'height') {
                iframe.style[key] = value + 'px'
              }
            }
          })

          // Update caption text if it exists
          if (captionEl && newAttrs.caption !== undefined) {
            if (document.activeElement !== captionEl) {
              captionEl.textContent = newAttrs.caption || 'Enter caption...'
              captionEl.style.color = newAttrs.caption ? '#6b7280' : '#9ca3af'
            }
          }

          return true
        },
      }
    }
  },
  addCommands() {
    return {
      setResizableVideo: (options) => ({ commands }) => {
        // Convert YouTube URL to embed format
        let embedSrc = options.src
        if (embedSrc.includes('youtube.com/watch?v=')) {
          const videoId = embedSrc.split('v=')[1].split('&')[0]
          embedSrc = `https://www.youtube.com/embed/${videoId}`
        } else if (embedSrc.includes('youtu.be/')) {
          const videoId = embedSrc.split('youtu.be/')[1].split('?')[0]
          embedSrc = `https://www.youtube.com/embed/${videoId}`
        }

        return commands.insertContent({
          type: this.name,
          attrs: {
            display: 'block',
            align: 'left',
            caption: '',
            captionPosition: 'none',
            spacing: 'medium',
            ...options,
            src: embedSrc,
          },
        })
      },
    }
  },
})